import type { BankTransactionStorageModule } from '~/features/transactions/storage'
import type {
    UserBankAccount,
    BankTransaction,
    DocumentTransactionAssociation,
} from '~/storage/types'
import type { CacheService } from './cache/cache'
import type { Services } from './types'
import {
    getAllCategories,
    getCategoriesForLLM,
    type TransactionCategoryType,
} from '~/features/transactions/services/categories'
import type {
    LLMResponseJSONSchema,
    LLMRequestMessage,
} from '~common/llms/types'

export class BankTransactionService {
    constructor(
        private storage: BankTransactionStorageModule,
        private cache: CacheService,
        private getServices?: () => Pick<Services, 'llms'>,
    ) {}

    async saveTransactions(
        transactions: Omit<BankTransaction, 'id'>[],
    ): ReturnType<
        typeof BankTransactionStorageModule.prototype.saveTransactions
    > {
        const result = await this.storage.saveTransactions(transactions)
        // TODO-audit: This is reading all transactions from the database to update the cache every time this is called. Likely to hit space+Firestre limits
        const updatedTransactions = await this.storage.getAllTransactions()
        await this.cache.setTransaction(updatedTransactions)
        return result
    }

    /**
     * Assigns a bookkeeping category to a transaction using an LLM.
     * Replaces any existing category.
     */
    async assignCategoryAfterAssociation(transactionId: string): Promise<void> {
        const llms = this.getServices?.().llms
        if (!llms) return

        const tx = await this.getTransactionById(transactionId)
        if (!tx) return

        // Fetch matched invoice and get reason from it
        let invoiceReason = ''
        let invoiceVendor = ''
        const matchesByTransaction =
            this.cache.getKey('matchesByTransaction') || {}
        const assocIds = matchesByTransaction[transactionId] || []
        const documentTransactionAssociations =
            this.cache.getKey('documentTransactionAssociations') || {}

        // Find the first user-confirmed or auto association (prefer user-confirmed)
        const associations = assocIds
            .map((id) => documentTransactionAssociations[id])
            .filter((assoc): assoc is DocumentTransactionAssociation =>
                Boolean(assoc),
            )
            .sort((a, b) => {
                // Prefer user_confirmed over auto
                if (
                    a.method === 'user_confirmed' &&
                    b.method !== 'user_confirmed'
                )
                    return -1
                if (
                    b.method === 'user_confirmed' &&
                    a.method !== 'user_confirmed'
                )
                    return 1
                return (b.confidence ?? 0) - (a.confidence ?? 0)
            })

        let vatRate: number | null = null
        if (associations.length > 0) {
            const matchedInvoiceId = associations[0].documentId
            const invoices = this.cache.getKey('invoices') || {}
            const invoice = invoices[matchedInvoiceId]
            vatRate = invoice?.metadata?.vatRate
            if (invoice?.metadata?.vendorName) {
                invoiceVendor = invoice.metadata.vendorName
            }
            if (invoice?.metadata?.reason) {
                invoiceReason = invoice.metadata.reason
            }
        }

        const categoryType: TransactionCategoryType =
            tx.amount >= 0 ? 'REVENUE' : 'EXPENSE'
        const settings = this.cache.getKey('settings')
        if (!settings?.plugins?.['datev-classify-transactions']?.config) return
        const categoriesJSON = getCategoriesForLLM(
            categoryType,
            settings.plugins?.['datev-classify-transactions']?.config,
        )

        const masterPrompt = SYSTEM_BOOKING_SYSTEM_PROMPT

        const userPrompt =
            settings?.plugins?.['datev-classify-transactions']?.config?.prompt
        const transactionInformation = {
            ref: tx.referenceText,
            note: tx.note,
            vendor: invoiceVendor + ' - ' + tx.vendorName,
            reason: invoiceReason,
            ...(vatRate ? { vatRate } : {}),
        }

        const messages: LLMRequestMessage<string>[] = [
            { role: 'system' as const, content: masterPrompt },
            {
                role: 'user' as const,
                content: `TRANSACTION_INFORMATION: ${JSON.stringify(transactionInformation)}`,
            },
            {
                role: 'user' as const,
                content: `AVAILABLE_BOOKING_ACCOUNTS (JSON):\n${categoriesJSON}`,
            },
            ...(userPrompt
                ? [
                      {
                          role: 'user' as const,
                          content: `USER_INSTRUCTIONS: \n${userPrompt}\n`,
                      },
                  ]
                : []),
        ]

        type Response = { id: number }
        const jsonResponseSchema: LLMResponseJSONSchema = {
            name: 'transaction_category_selection',
            strict: true,
            schema: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description:
                            'the id of the selected bookkeeping account',
                    },
                },
                required: ['id'],
                additionalProperties: false,
            },
        }

        try {
            // mark running
            const current = this.cache.getData().categoryMatching || {}
            this.cache.setKeys({
                categoryMatching: { ...current, [transactionId]: 'running' },
            })

            const allCategories = getAllCategories(settings?.bookingCategories)
            const maxRetries = 2
            let categoryId: number | undefined
            let result: Response | undefined

            for (let attempt = 0; attempt < maxRetries; attempt++) {
                result = (await llms.getReply<Response>({
                    model: 'x-ai/grok-4.1-fast',
                    messages,
                    temperature: 0,
                    jsonResponseSchema,
                    expectedResponse: 'json',
                    reasoningEffort: 'low',
                    stream: false,
                })) as Response

                categoryId =
                    typeof result === 'object' && result && 'id' in result
                        ? Number((result as Response).id)
                        : undefined

                if (
                    typeof categoryId === 'number' &&
                    Number.isFinite(categoryId) &&
                    categoryId.toString() in allCategories
                ) {
                    break
                }
            }

            const after = this.cache.getData().categoryMatching || {}
            if (
                typeof categoryId === 'number' &&
                Number.isFinite(categoryId) &&
                categoryId.toString() in allCategories
            ) {
                await this.update(transactionId, { category: categoryId })
            }
            this.cache.setKeys({
                categoryMatching: { ...after, [transactionId]: 'success' },
            })
        } catch (e) {
            // Intentionally swallow errors; categorization is best-effort
            const after = this.cache.getData().categoryMatching || {}
            this.cache.setKeys({
                categoryMatching: { ...after, [transactionId]: 'error' },
            })
        }
    }

    async update(
        id: string,
        updates: Partial<
            Omit<BankTransaction, 'id' | 'createdAt' | 'updatedAt'>
        >,
    ): Promise<void> {
        const currentTransactions = this.cache.getData().transactions ?? {}
        const existingTransaction = currentTransactions[id]

        if (existingTransaction) {
            const updatedTransaction = { ...existingTransaction }
            for (const key in updates) {
                const value = updates[key as keyof typeof updates]
                if (value === undefined) {
                    delete updatedTransaction[key as keyof BankTransaction]
                } else {
                    ;(updatedTransaction as Record<string, unknown>)[key] =
                        value
                }
            }
            await this.cache.setTransaction([{ ...updatedTransaction, id }])
        } else {
            const cacheUpdates: Partial<BankTransaction> = { id }
            for (const key in updates) {
                const value = updates[key as keyof typeof updates]
                if (value !== undefined) {
                    ;(cacheUpdates as Record<string, unknown>)[key] = value
                }
            }
            await this.cache.setTransaction([cacheUpdates as BankTransaction])
        }
        this.storage.update(id, updates)
    }

    async delete(id: string): Promise<void> {
        await this.storage.delete(id)
        const currentTransactions = this.cache.getData().transactions ?? {}
        const updatedTransactions = { ...currentTransactions }
        delete updatedTransactions[id]
        this.cache.setKeys({ transactions: updatedTransactions })
        this.cache.onTransactionsUpdate.emit(new Set([id]))
    }

    async getTransactionById(id: string): Promise<BankTransaction | null> {
        return this.storage.getTransactionById(id)
    }

    // Bank Account methods
    async getAllBankAccounts(): Promise<UserBankAccount[]> {
        const accounts = await this.storage.getAllBankAccounts()
        // Update cache if not already
        const currentBankAccounts = this.cache.getData().bankAccounts ?? {}
        const cacheUpdates: Record<string, UserBankAccount> = {}
        for (const account of accounts) {
            cacheUpdates[account.iban] = account
        }
        if (Object.keys(cacheUpdates).length > 0) {
            this.cache.setKeys({
                bankAccounts: { ...currentBankAccounts, ...cacheUpdates },
            })
        }
        return accounts
    }

    async saveBankAccount(
        account: Omit<UserBankAccount, 'createdAt' | 'updatedAt'>,
    ): Promise<void> {
        await this.storage.saveBankAccount(account)
        // Update cache
        const updatedAccounts = await this.storage.getAllBankAccounts()
        this.updateBankAccountsCache(updatedAccounts)
    }

    async deleteBankAccount(id: string): Promise<void> {
        await this.storage.deleteBankAccount(id)
        // Update cache
        const currentBankAccounts = this.cache.getData().bankAccounts ?? {}
        const updatedBankAccounts = { ...currentBankAccounts }
        delete updatedBankAccounts[id]
        this.cache.setKeys({ bankAccounts: updatedBankAccounts })
    }

    private updateBankAccountsCache(accounts: UserBankAccount[]) {
        const cacheUpdates: Record<string, UserBankAccount> = {}
        for (const account of accounts) {
            cacheUpdates[account.iban] = account
        }
        const current = this.cache.getData().bankAccounts ?? {}
        this.cache.setKeys({
            bankAccounts: { ...current, ...cacheUpdates },
        })
    }
}

export const SYSTEM_BOOKING_SYSTEM_PROMPT = `You are an expert financial classification system. Your task is to match transaction descriptions with appropriate accounting booking guides.

## TASK
Analyze the given transaction information and find the most suitable bookkeeping accounts from the provided list. 

1. First analyze the transaction information and understand more about the context of it. 
Ask yourself:
- Which brands do you recognise and what is their field of business/service? 
- Which type of service is it? Subscription, Product, Service, etc.
- Which type of product do you recognise? 
2. Your intermediate goal is to find the 3 top matching bookkeeping accounts from the available accounts and according to the user instructions. Give each of your picks a rating from 0 (low) and 5 (high) with a short explanation why you gave that rating based on how good it is a match for the context you analysed and the instructions of the user.
3. Return the top pick's id. 

## OUTPUT FORMAT
Return the top match as a JSON with this structure, and no other text or formatting.
{id: the id of the bookkeeping account}

## INPUT FORMATS
In the next messages you'll receive the information to process: 
2. The transaction information: 
{
  "vendor": string, // the name of the vendor/company/person/etc.
  "note": string, // The note field should have extremely high priority in your consideration for the category as it often contains human provided labels and context for a transaction
  "ref": string // The transactions reference text,
  "reason": string, // the associted invoice reason
  "vatRate": number | null, // the VAT rate of the invoice, sometimes necessry to distinguish between categories with specific VAT rates
}
2. the booking accounts are an array of json object with the following structure
[
    {
        id: number,
        label: string,
        info: string // optional containing more context for the category,
    }
]
3. Instructions by the user. Those should be treated with higher priority than examples in the booking account dictionary if there are conflicting requirements.
`
